import React from "react";
import { BookOpen } from "lucide-react";
import { useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface DocsHelpLinkProps {
  /**
   * Page slug with an optional anchor, e.g. `concepts/conflicts#replace-from-disk`. Must be a string literal:
   * `DocsContentTests.Every_Portal_Help_Link_Resolves` reads it from the source and fails the build when the page or
   * heading no longer exists.
   */
  to: string;
  label?: string;
  className?: string;
}

/**
 * The `?` that opens the documentation for the screen it sits on. It opens a new tab so the reader keeps whatever
 * they were in the middle of — an open drawer, a half-filled form, filters.
 */
const DocsHelpLink: React.FC<DocsHelpLinkProps> = ({ to, label = "Open the documentation for this screen", className }) => {
  const { organizationId } = useParams<{ organizationId?: string }>();
  const base = organizationId ? `/organization/${organizationId}/docs` : "/docs";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button asChild variant="ghost" size="icon" className={cn("size-7 shrink-0 text-muted-foreground hover:text-foreground", className)}>
          <a href={`${base}/${to}`} target="_blank" rel="noreferrer" aria-label={label}>
            <BookOpen className="size-4" />
          </a>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};

export default DocsHelpLink;
